import { DataSourceType, FilterOperatorType, FilterOperandType } from "@coeff/api";

// Used to group FilterOperatorTypes into
// groups whose operand types match exactly
export enum FilterOperandTypeGroup {
  EMPTY = "EMPTY",
  NUMBER_SINGLE = "SINGLE_NUMBER",
  STRING_SINGLE = "SINGLE_STRING",
  PICKLIST = "PICKLIST",
  MULTIPICKLIST = "MULTIPICKLIST",
  DATE_RELATIVE = "DATE_RELATIVE",
  DATE_EXACT = "DATE_EXACT",
  DATE_WITHIN = "DATE_WITHIN",
}

export type FilterOperator = {
  dataKey: FilterOperatorType;
  label: string;
  operandType: FilterOperandType;
  group: string;
  hideValue: boolean;
  onlySupportedBy?: DataSourceType[];
  notSupportedBy?: DataSourceType[];
  operandTypeGroup: FilterOperandTypeGroup;
  sortOrder?: number;
};

export const HUBSPOT_FILTERS: Array<FilterOperator> = [
  {
    dataKey: "number_is_between",
    label: "is between",
    group: "number",
    operandType: "text",
    hideValue: false,
    onlySupportedBy: [DataSourceType.Hubspot],
    operandTypeGroup: FilterOperandTypeGroup.EMPTY,
    sortOrder: 6,
  },
];

export const LOOKER_FILTERS: Array<FilterOperator> = [
  {
    dataKey: "text_is_null",
    label: "is null",
    operandType: "text",
    group: "text",
    hideValue: true,
    operandTypeGroup: FilterOperandTypeGroup.EMPTY,
    onlySupportedBy: [DataSourceType.Looker],
    sortOrder: 7,
  },
  {
    dataKey: "text_is_not_null",
    label: "is not null",
    operandType: "text",
    group: "text",
    hideValue: true,
    operandTypeGroup: FilterOperandTypeGroup.EMPTY,
    onlySupportedBy: [DataSourceType.Looker],
    sortOrder: 14,
  },
  {
    dataKey: "text_is_anyvalue",
    label: "is any value",
    operandType: "text",
    group: "text",
    hideValue: true,
    operandTypeGroup: FilterOperandTypeGroup.EMPTY,
    onlySupportedBy: [DataSourceType.Looker],
    sortOrder: 98,
  },
  {
    dataKey: "number_is_null",
    label: "is null",
    operandType: "number",
    group: "number",
    hideValue: true,
    operandTypeGroup: FilterOperandTypeGroup.EMPTY,
    onlySupportedBy: [DataSourceType.Looker],
    sortOrder: 7,
  },
  {
    dataKey: "number_is_not_null",
    label: "is not null",
    operandType: "number",
    group: "number",
    hideValue: true,
    operandTypeGroup: FilterOperandTypeGroup.EMPTY,
    onlySupportedBy: [DataSourceType.Looker],
    sortOrder: 10,
  },
  {
    dataKey: "number_is_not_one_of",
    label: "is not one of",
    group: "number",
    operandType: "text",
    hideValue: false,
    operandTypeGroup: FilterOperandTypeGroup.PICKLIST,
    onlySupportedBy: [DataSourceType.Looker],
    sortOrder: 8,
  },
  {
    dataKey: "number_is_between",
    label: "is between",
    group: "number",
    operandType: "text",
    hideValue: false,
    onlySupportedBy: [DataSourceType.Looker],
    operandTypeGroup: FilterOperandTypeGroup.EMPTY,
    sortOrder: 6,
  },
  {
    dataKey: "number_is_not_between",
    label: "is not between",
    group: "number",
    operandType: "text",
    hideValue: false,
    onlySupportedBy: [DataSourceType.Looker],
    operandTypeGroup: FilterOperandTypeGroup.EMPTY,
    sortOrder: 9,
  },
  {
    dataKey: "number_matches_advanced",
    label: "matches (advanced)",
    group: "number",
    operandType: "text",
    hideValue: false,
    onlySupportedBy: [DataSourceType.Looker],
    operandTypeGroup: FilterOperandTypeGroup.STRING_SINGLE,
    sortOrder: 12,
  },
  {
    dataKey: "text_is_not_one_of",
    label: "is not one of",
    group: "text",
    operandType: "text",
    hideValue: false,
    operandTypeGroup: FilterOperandTypeGroup.PICKLIST,
    onlySupportedBy: [DataSourceType.Looker],
    sortOrder: 10,
  },
  {
    dataKey: "text_starts_with",
    label: "starts with",
    group: "text",
    operandType: "text",
    hideValue: false,
    operandTypeGroup: FilterOperandTypeGroup.PICKLIST,
    onlySupportedBy: [DataSourceType.Looker, DataSourceType.Sage],
    sortOrder: 3,
  },
  {
    dataKey: "text_does_not_starts_with",
    label: "does not start with",
    group: "text",
    operandType: "text",
    hideValue: false,
    operandTypeGroup: FilterOperandTypeGroup.PICKLIST,
    onlySupportedBy: [DataSourceType.Looker, DataSourceType.Sage],
    sortOrder: 11,
  },
  {
    dataKey: "text_ends_with",
    label: "ends with",
    group: "text",
    operandType: "text",
    hideValue: false,
    operandTypeGroup: FilterOperandTypeGroup.PICKLIST,
    onlySupportedBy: [DataSourceType.Looker],
    sortOrder: 4,
  },
  {
    dataKey: "text_does_not_ends_with",
    label: "does not end with",
    group: "text",
    operandType: "text",
    hideValue: false,
    operandTypeGroup: FilterOperandTypeGroup.PICKLIST,
    onlySupportedBy: [DataSourceType.Looker],
    sortOrder: 12,
  },
  {
    dataKey: "text_is_blank",
    label: "is blank",
    operandType: "text",
    group: "text",
    hideValue: true,
    operandTypeGroup: FilterOperandTypeGroup.EMPTY,
    onlySupportedBy: [DataSourceType.Looker],
    sortOrder: 6,
  },
  {
    dataKey: "text_is_not_blank",
    label: "is not blank",
    operandType: "text",
    group: "text",
    hideValue: true,
    operandTypeGroup: FilterOperandTypeGroup.EMPTY,
    onlySupportedBy: [DataSourceType.Looker],
    sortOrder: 13,
  },
  {
    dataKey: "text_matches_advanced",
    label: "matches (advanced)",
    operandType: "text",
    group: "text",
    hideValue: false,
    operandTypeGroup: FilterOperandTypeGroup.STRING_SINGLE,
    onlySupportedBy: [DataSourceType.Looker],
    sortOrder: 99,
  },
  {
    dataKey: "date_is_on_the_day",
    label: "is on the day",
    group: "date",
    operandType: "date",
    hideValue: false,
    operandTypeGroup: FilterOperandTypeGroup.STRING_SINGLE,
    onlySupportedBy: [DataSourceType.Looker, DataSourceType.Snowflake],
    sortOrder: 2,
  },
  {
    dataKey: "date_is_in_the_year",
    label: "is in the year",
    group: "date",
    operandType: "date",
    hideValue: false,
    operandTypeGroup: FilterOperandTypeGroup.STRING_SINGLE,
    onlySupportedBy: [DataSourceType.Looker],
    sortOrder: 7,
  },
  {
    dataKey: "date_is_in_the_month",
    label: "is in the month",
    group: "date",
    operandType: "date",
    hideValue: false,
    operandTypeGroup: FilterOperandTypeGroup.STRING_SINGLE,
    onlySupportedBy: [DataSourceType.Looker],
    sortOrder: 8,
  },
  {
    dataKey: "date_is_this",
    label: "is this",
    group: "date",
    operandType: "date",
    hideValue: false,
    operandTypeGroup: FilterOperandTypeGroup.STRING_SINGLE,
    onlySupportedBy: [DataSourceType.Looker],
    sortOrder: 9,
  },
  {
    dataKey: "date_is_next",
    label: "is next",
    group: "date",
    operandType: "date",
    hideValue: false,
    operandTypeGroup: FilterOperandTypeGroup.STRING_SINGLE,
    onlySupportedBy: [DataSourceType.Looker],
    sortOrder: 10,
  },
  {
    dataKey: "date_is_previous",
    label: "is previous",
    group: "date",
    operandType: "date",
    hideValue: false,
    operandTypeGroup: FilterOperandTypeGroup.STRING_SINGLE,
    onlySupportedBy: [DataSourceType.Looker],
    sortOrder: 11,
  },
  {
    dataKey: "date_matches_advanced",
    label: "matches (advanced)",
    group: "date",
    operandType: "date",
    hideValue: false,
    operandTypeGroup: FilterOperandTypeGroup.STRING_SINGLE,
    onlySupportedBy: [DataSourceType.Looker],
    sortOrder: 99,
  },
  {
    dataKey: "date_is_anytime",
    label: "is anytime",
    group: "date",
    operandType: "date",
    hideValue: true,
    operandTypeGroup: FilterOperandTypeGroup.EMPTY,
    onlySupportedBy: [DataSourceType.Looker],
    sortOrder: 98,
  },
  {
    dataKey: "date_is_in_the_last",
    label: "is in the last",
    group: "date",
    operandType: "date",
    hideValue: false,
    operandTypeGroup: FilterOperandTypeGroup.EMPTY,
    onlySupportedBy: [DataSourceType.Looker],
    sortOrder: 1,
  },
  {
    dataKey: "date_is",
    label: "is",
    group: "date",
    operandType: "date",
    hideValue: false,
    operandTypeGroup: FilterOperandTypeGroup.PICKLIST,
    onlySupportedBy: [DataSourceType.Looker],
    sortOrder: 12,
  },
  {
    dataKey: "location_is_anywhere",
    label: "is anywhere",
    group: "location",
    operandType: "text",
    hideValue: true,
    operandTypeGroup: FilterOperandTypeGroup.EMPTY,
    onlySupportedBy: [DataSourceType.Looker],
    sortOrder: 4,
  },
  {
    dataKey: "location_is_null",
    label: "is null",
    group: "location",
    operandType: "text",
    hideValue: true,
    operandTypeGroup: FilterOperandTypeGroup.EMPTY,
    onlySupportedBy: [DataSourceType.Looker],
    sortOrder: 5,
  },
  {
    dataKey: "location_is_not_null",
    label: "is not null",
    group: "location",
    operandType: "text",
    hideValue: true,
    operandTypeGroup: FilterOperandTypeGroup.EMPTY,
    onlySupportedBy: [DataSourceType.Looker],
    sortOrder: 6,
  },
  {
    dataKey: "location_is_exactly",
    label: "is exactly",
    group: "location",
    operandType: "text",
    hideValue: false,
    operandTypeGroup: FilterOperandTypeGroup.STRING_SINGLE,
    onlySupportedBy: [DataSourceType.Looker],
    sortOrder: 1,
  },
  {
    dataKey: "location_is_in_the_box",
    label: "is in the box",
    group: "location",
    operandType: "text",
    hideValue: false,
    operandTypeGroup: FilterOperandTypeGroup.STRING_SINGLE,
    onlySupportedBy: [DataSourceType.Looker],
    sortOrder: 2,
  },
  {
    dataKey: "location_is_in_the_circle",
    label: "is in the circle",
    group: "location",
    operandType: "text",
    hideValue: false,
    operandTypeGroup: FilterOperandTypeGroup.STRING_SINGLE,
    onlySupportedBy: [DataSourceType.Looker],
    sortOrder: 3,
  },
  {
    dataKey: "location_matches_advanced",
    label: "matches (advanced)",
    group: "location",
    operandType: "text",
    hideValue: false,
    operandTypeGroup: FilterOperandTypeGroup.STRING_SINGLE,
    onlySupportedBy: [DataSourceType.Looker],
    sortOrder: 7,
  },
  {
    dataKey: "picklist_is_any_value",
    label: "is any value",
    group: "picklist",
    operandType: "text",
    hideValue: true,
    operandTypeGroup: FilterOperandTypeGroup.EMPTY,
    onlySupportedBy: [DataSourceType.Looker],
    sortOrder: 98,
  },
];
